import { useState, useEffect } from 'react';
import { adminGetRevenue } from '../../lib/adminApi';
import AdminSidebar from '../../components/AdminSidebar';

const NAVY   = '#0a2540';
const ORANGE = '#F37428';
const BLUE   = '#0176CE';
const GRAY   = '#94a3b8';

const PRESETS = ['Today', '7D', '30D', '90D', 'YTD', 'All'];

function fmt$(n) { return n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtDate(raw) { if (!raw) return '—'; return new Date(raw).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
function toISODate(d) { return d.toISOString().slice(0, 10); }

function calcRange(preset) {
  const now = new Date();
  const today = toISODate(now);
  if (preset === 'Today') return { from: today, to: today };
  if (preset === '7D')    { const d = new Date(now); d.setDate(d.getDate() - 6);  return { from: toISODate(d), to: today }; }
  if (preset === '30D')   { const d = new Date(now); d.setDate(d.getDate() - 29); return { from: toISODate(d), to: today }; }
  if (preset === '90D')   { const d = new Date(now); d.setDate(d.getDate() - 89); return { from: toISODate(d), to: today }; }
  if (preset === 'YTD')   { return { from: `${now.getFullYear()}-01-01`, to: today }; }
  return { from: '', to: '' };
}

function MonthlyChart({ monthly }) {
  if (!monthly || monthly.length === 0) return <div style={{ color: '#94a3b8', fontSize: 13, padding: 24, textAlign: 'center' }}>No monthly data</div>;

  const months = monthly.slice(-6);
  const maxVal = Math.max(...months.flatMap(m => [m.revenue || 0, m.cost || 0, m.profit || 0]), 1);

  const chartH   = 160;
  const barW     = 18;
  const groupGap = 48;
  const groupW   = barW * 3 + 8;
  const padLeft  = 50;
  const padBot   = 36;
  const padTop   = 16;
  const totalW   = padLeft + months.length * (groupW + groupGap) + 20;
  const totalH   = chartH + padBot + padTop;

  function barH(v) { return Math.max(2, ((v || 0) / maxVal) * chartH); }
  function barY(v) { return padTop + chartH - barH(v); }

  // Y-axis tick values
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(f => ({ val: maxVal * f, y: padTop + chartH - f * chartH }));

  return (
    <svg width="100%" height="260" viewBox={`0 0 ${totalW} ${totalH}`} preserveAspectRatio="xMidYMid meet" style={{ display: 'block', maxWidth: '100%' }}>
      {/* Grid lines + y labels */}
      {ticks.map((t, i) => (
        <g key={i}>
          <line x1={padLeft} y1={t.y} x2={totalW - 20} y2={t.y} stroke="#f1f5f9" strokeWidth={1} />
          <text x={padLeft - 6} y={t.y + 4} textAnchor="end" fontSize={9} fill="#94a3b8">
            {t.val >= 1000 ? `$${(t.val / 1000).toFixed(0)}k` : `$${Math.round(t.val)}`}
          </text>
        </g>
      ))}

      {/* Bars */}
      {months.map((m, i) => {
        const x = padLeft + i * (groupW + groupGap);
        return (
          <g key={i}>
            {/* Revenue bar */}
            <rect x={x} y={barY(m.revenue)} width={barW} height={barH(m.revenue)} fill={BLUE} rx={3} />
            {/* Cost bar */}
            <rect x={x + barW + 4} y={barY(m.cost)} width={barW} height={barH(m.cost)} fill={GRAY} rx={3} />
            {/* Profit bar */}
            <rect x={x + barW * 2 + 8} y={barY(m.profit)} width={barW} height={barH(m.profit)} fill={ORANGE} rx={3} />
            {/* Month label */}
            <text x={x + groupW / 2} y={padTop + chartH + 18} textAnchor="middle" fontSize={10} fill="#64748b">
              {m.month_label || m.month || ''}
            </text>
          </g>
        );
      })}

      {/* Legend */}
      <g transform={`translate(${padLeft}, ${totalH - 8})`}>
        <rect x={0} y={0} width={10} height={10} fill={BLUE}   rx={2} />
        <text x={14} y={9} fontSize={10} fill="#64748b">Revenue</text>
        <rect x={70} y={0} width={10} height={10} fill={GRAY}   rx={2} />
        <text x={84} y={9} fontSize={10} fill="#64748b">Cost</text>
        <rect x={120} y={0} width={10} height={10} fill={ORANGE} rx={2} />
        <text x={134} y={9} fontSize={10} fill="#64748b">Profit</text>
      </g>
    </svg>
  );
}

export default function AdminRevenue() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [dateRange, setDateRange] = useState(() => {
    const { from, to } = calcRange('30D');
    return { from, to, preset: '30D' };
  });

  useEffect(() => {
    setLoading(true);
    setError('');
    const params = {};
    if (dateRange.from) params.from = dateRange.from;
    if (dateRange.to)   params.to   = dateRange.to;
    adminGetRevenue(params)
      .then(res => { setData(res); setLoading(false); })
      .catch(e  => { setError(e.message); setLoading(false); });
  }, [dateRange]);

  function handlePreset(preset) {
    const { from, to } = calcRange(preset);
    setDateRange({ from, to, preset });
  }

  const [sortCol, setSortCol] = useState('paid_on_utc');
  const [sortDir, setSortDir] = useState('desc');
  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  function handleCustomFrom(e) { setDateRange(prev => ({ ...prev, from: e.target.value, preset: 'custom' })); }
  function handleCustomTo(e)   { setDateRange(prev => ({ ...prev, to:   e.target.value, preset: 'custom' })); }

  const summary   = data?.summary   || {};
  const rawDaily  = data?.daily     || [];
  const daily = rawDaily.slice().sort((a, b) => {
    let av = a[sortCol] ?? '', bv = b[sortCol] ?? '';
    if (typeof av === 'number' || typeof bv === 'number') { av = Number(av||0); bv = Number(bv||0); }
    else { av = String(av).toLowerCase(); bv = String(bv).toLowerCase(); }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });
  const monthly   = data?.monthly   || [];
  const costBreak = data?.cost_breakdown || [];
  const discSum   = data?.discount_summary || {};

  return (
    <div style={s.page}>
      <AdminSidebar active="revenue" />
      <main style={s.main}>
        <div style={s.header}>
          <h1 style={s.title}>Revenue</h1>
          <p style={s.sub}>Payment history and financial overview</p>
        </div>

        {/* Date range controls */}
        <div style={s.rangeBar}>
          <div style={s.presets}>
            {PRESETS.map(p => (
              <button
                key={p}
                onClick={() => handlePreset(p)}
                style={{
                  ...s.presetBtn,
                  background: dateRange.preset === p ? NAVY : '#fff',
                  color:      dateRange.preset === p ? '#fff' : '#374151',
                  border:     `1px solid ${dateRange.preset === p ? NAVY : '#e2e8f0'}`,
                }}
              >{p}</button>
            ))}
          </div>
          <div style={s.customDates}>
            <span style={s.dateLabel}>From</span>
            <input type="date" style={s.dateInput} value={dateRange.from} onChange={handleCustomFrom} />
            <span style={s.dateLabel}>To</span>
            <input type="date" style={s.dateInput} value={dateRange.to}   onChange={handleCustomTo} />
          </div>
        </div>

        {loading && <div style={{ color: '#64748b', padding: 32 }}>Loading…</div>}
        {error   && <div style={{ color: '#ef4444', padding: 32 }}>{error}</div>}

        {!loading && !error && (
          <>
            {/* Stat cards */}
            <div style={s.statsRow}>
              {[
                { label: 'Total Revenue', value: fmt$(summary.total_revenue), color: BLUE   },
                { label: 'Total Cost',    value: fmt$(summary.total_cost),    color: ORANGE },
                { label: 'Total Profit',  value: fmt$(summary.total_profit),  color: BLUE   },
                { label: 'Payments',      value: summary.payment_count ?? '—', color: ORANGE },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, borderTop: `4px solid ${color}`, padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                  <div style={{ fontSize: 26, fontWeight: 700, color, letterSpacing: '-0.5px', marginBottom: 2 }}>{value}</div>
                  <div style={{ color: '#64748b', fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Monthly chart */}
            <div style={{ ...s.card, marginBottom: 20 }}>
              <div style={s.cardHead}><span style={s.cardTitle}>Monthly Revenue / Cost / Profit</span></div>
              <div style={{ padding: '20px 24px' }}>
                <MonthlyChart monthly={monthly} />
              </div>
            </div>

            {/* Cost breakdown */}
            <div style={{ ...s.card, marginBottom: 20 }}>
              <div style={s.cardHead}><span style={s.cardTitle}>Cost Breakdown</span></div>
              <table style={s.table}>
                <thead>
                  <tr>{['Enrichment Type', 'Records', 'Tokens', 'Est. Cost'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {costBreak.length === 0 && <tr><td colSpan={4} style={{ ...s.td, color: '#94a3b8', textAlign: 'center', padding: 24 }}>No cost data</td></tr>}
                  {costBreak.map((row, i) => (
                    <tr key={i} style={s.tr}>
                      <td style={{ ...s.td, fontWeight: 500, color: NAVY }}>{row.source_label || row.source || '—'}</td>
                      <td style={s.td}>{(row.record_count || 0).toLocaleString()}</td>
                      <td style={s.td}>{(row.total_tokens || 0).toLocaleString()}</td>
                      <td style={{ ...s.td, color: '#059669', fontWeight: 600 }}>{fmt$(row.estimated_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Discount summary */}
            {(discSum.global_count != null || discSum.tenant_count != null) && (
              <div style={{ background: 'rgba(1,118,206,0.05)', border: '1px solid rgba(1,118,206,0.2)', borderRadius: 10, padding: '14px 20px', marginBottom: 20, display: 'flex', gap: 32, flexWrap: 'wrap', fontSize: 13, color: NAVY }}>
                <span>Global codes used: <strong>{discSum.global_count ?? 0}</strong> times</span>
                <span>Tenant codes used: <strong>{discSum.tenant_count ?? 0}</strong> times</span>
                <span>Total discounts given: <strong style={{ color: ORANGE }}>{fmt$(discSum.total_discount_amount)}</strong></span>
              </div>
            )}

            {/* Payment history */}
            <div style={s.card}>
              <div style={s.cardHead}><span style={s.cardTitle}>Payment History</span></div>
              <table style={s.table}>
                <thead>
                  <tr>
                    {[
                      { label: 'Date',            col: 'paid_on_utc'      },
                      { label: 'Tenant',          col: 'company_name'     },
                      { label: 'File',            col: 'file_name'        },
                      { label: 'Records',         col: 'record_count'     },
                      { label: 'Revenue',         col: 'amount_usd'       },
                      { label: 'Discount Used',   col: 'discount_code'    },
                      { label: 'Discount Amount', col: 'discount_amount'  },
                    ].map(({ label, col }) => (
                      <th key={label} style={{ ...s.th, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => toggleSort(col)}>
                        {label} {sortCol === col ? (sortDir === 'asc' ? '↑' : '↓') : <span style={{ color: '#d1d5db' }}>↕</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {daily.map((row, i) => (
                    <tr key={i} style={s.tr}>
                      <td style={{ ...s.td, color: '#94a3b8', fontSize: 12 }}>{fmtDate(row.payment_date || row.created_on_utc)}</td>
                      <td style={{ ...s.td, fontWeight: 500, color: NAVY }}>{row.company_name || '—'}</td>
                      <td style={{ ...s.td, color: '#64748b', fontSize: 12, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.file_name || '—'}</td>
                      <td style={s.td}>{(row.record_count || 0).toLocaleString()}</td>
                      <td style={{ ...s.td, color: '#059669', fontWeight: 600 }}>{fmt$(row.amount_usd)}</td>
                      <td style={{ ...s.td, color: '#64748b', fontSize: 12 }}>{row.discount_code || '—'}</td>
                      <td style={{ ...s.td, color: row.discount_amount ? ORANGE : '#94a3b8', fontWeight: row.discount_amount ? 600 : 400, fontSize: 12 }}>
                        {row.discount_amount ? fmt$(row.discount_amount) : '—'}
                      </td>
                    </tr>
                  ))}
                  {daily.length === 0 && (
                    <tr><td colSpan={7} style={{ ...s.td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>No payments recorded</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const s = {
  page:       { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main:       { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  header:     { marginBottom: 20 },
  title:      { color: NAVY, fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: '-0.4px' },
  sub:        { color: '#64748b', fontSize: 13, margin: '4px 0 0' },
  rangeBar:   { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 16px', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  presets:    { display: 'flex', gap: 6 },
  presetBtn:  { borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 500, padding: '5px 12px', transition: 'all 0.12s' },
  customDates:{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' },
  dateLabel:  { color: '#94a3b8', fontSize: 12, fontWeight: 500 },
  dateInput:  { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 7, color: NAVY, fontSize: 12, padding: '5px 10px', outline: 'none' },
  statsRow:   { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 20 },
  card:       { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  cardHead:   { padding: '16px 20px', borderBottom: '1px solid #e2e8f0' },
  cardTitle:  { color: NAVY, fontWeight: 600, fontSize: 14 },
  table:      { width: '100%', borderCollapse: 'collapse' },
  th:         { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 16px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  tr:         { borderBottom: '1px solid #f1f5f9' },
  td:         { color: '#374151', fontSize: 13, padding: '12px 16px', verticalAlign: 'middle' },
};
